import { TemplateResult, html, CSSResultGroup } from 'lit';
import { property, queryAssignedElements, state } from 'lit/decorators.js';
import { MOBILE_BREAK_POINT } from '../../util/common-modal';
import { customConverter } from '../../util/custom-converter';
import { BlocksElement } from '../BlocksElement';
import { BlocksAnchorNavItem } from '../anchor-nav-item/anchor-nav-item';
import type { BehaviorType, VariantType } from './anchor-nav.model';
import { KEYS } from '../../util/event-keys';
import styles from './anchor-nav.styles';
/**
 * @slot default - The component content
 * @csspart base - The component's base wrapper.
 * @csspart part-default - The component's default slot.
 */
export class BlocksAnchorNav extends BlocksElement {static styles: CSSResultGroup = styles;

  /**
   * Aria label attribute
   * - Provides an accessible name for the nav element
   */
  @property({ type: String })
  arialabel: string = 'Anchor';

  /**
   ***horizontal** renders the anchor-nav as horizontal variant.
   ***vertical** render the anchor-nav as vertical variant
   */
  @property({ type: String })
  variant: VariantType = 'horizontal';

  /**
   * Behavior variant.Defaults responsive
   */
  @property({ type: String })
  behavior: BehaviorType = 'responsive';

  /**
   * **Breakpoint below which anchor-nav switches to mobile view.Defaults to 768px.**
   */
  @property({ type: Number })
  mobileBreakPoint = MOBILE_BREAK_POINT;

  /**
   * @default true, This property is for controlling if scroll exists in initial load or not
   * If set to false, will not set any anchor nav item active, thereby preventing initial scroll
   */
  @property({
    // Allow "true|false" attribute values but keep the property boolean
    type: Boolean,
    converter: customConverter()
  })
  isItemsActive = true;

  /**
   * Query all the slotted Anchor Nav Items
   */
  @queryAssignedElements()
  private catAnchorNavItems: BlocksAnchorNavItem[] | undefined;

  @state()
  private isMobile = false;

  @state()
  private observer: IntersectionObserver | undefined;

  /**
   * Stores the current Active Anchor Nav Item
   */
  private currentNavItem?: BlocksAnchorNavItem | null;

  /**
   * Set the active nav item if the related section is 70% within the viewport.
   */
  private options = {
    threshold: 0.7
  };
  /**
   * Initialize navigation
   */
  connectedCallback() {
    super.connectedCallback();
    setTimeout(() => {
      this.setActive();
    }, 1);
    window.addEventListener('resize', this.triggerSmallScreen);
    this.triggerSmallScreen();
  }

  /**
   * First updated lifecycle hook
   * 1) Check if any anchor-nav-item is active or make first item as active.
   * 2) After active item is set, scroll to the respective section.
   */
  firstUpdated() {
    if (this.isItemsActive && this.catAnchorNavItems && this.catAnchorNavItems.length > 0) {
      const item = this.catAnchorNavItems.find((item) => item.isActive) || this.catAnchorNavItems[0]; /* 1 */
      this.changeActive(item);
      //Added setTimeOut since scroll is not happening on initial load intermittently.
      setTimeout(() => {
        item.scrollToSection(); /* 2 */
      });
    }
  }

  private triggerSmallScreen = () => {
    if (this.behavior !== 'responsive') {
      return;
    }
    this.isMobile = window.innerWidth < this.mobileBreakPoint;
  };

  /**
   * Disconnected callback lifecycle
   * 1) Remove window resize event listener
   */
  disconnectedCallback() {
    this.observer?.disconnect();
    super.disconnectedCallback();
    window.removeEventListener('resize', this.triggerSmallScreen);
  }

  /**
   * Set active
   * 1) Verify the element is intersecting and set the item to active
   * 2) Init the observer
   * 3) Target the elements to be observed
   * 4) If the currentNavItem goes out of viewport, currentNavItem is cleared.
   */
  private setActive() {
    const changeNav = (entries: IntersectionObserverEntry[]) => {
      const el = this;
      if (el) {
        this.observer?.observe(el);
      }

      entries.forEach((entry: IntersectionObserverEntry) => {
        const id = entry.target.getAttribute('id');
        const item = this.catAnchorNavItems?.find((item) => item.href === `#${id}`);
        if (item) {
          item.setAttribute('intersect-ratio', `${entry.intersectionRatio}`); /* 1 */
        }
      });

      if (this.currentNavItem) {
        const entry = entries.find((entry: IntersectionObserverEntry) => `#${entry.target.getAttribute('id')}` === this.currentNavItem?.href);
        if (entry && !entry.isIntersecting && entry.intersectionRatio < this.options.threshold) {
          this.currentNavItem = null; /* 4 */
          this.getActive();
        }
      } else {
        this.getActive();
      }
    };

    /* 2 */
    this.observer = new IntersectionObserver(changeNav, this.options);

    /* 3 */
    const sectionIds: string[] = [];
    this.catAnchorNavItems?.forEach((item) => {
      if (item.href) {
        sectionIds.push(item.href);
      }
    });

    if (sectionIds.length) {
      document.querySelectorAll<HTMLElement>(sectionIds.toString()).forEach((section) => {
        this.observer?.observe(section);
      });
    }
  }

  /**
   * FInd the active nav item whose related section is 70% within the viewport.
   */
  private getActive() {
    const item = this.catAnchorNavItems?.find((item) => +(item.getAttribute('intersect-ratio') ?? '0') > this.options.threshold);
    if (item) {
      this.changeActive(item);
    }
  }
  /**
   * Click Event Listener for Anchor Nav Items
   * @param event - MouseEvent emitted from anchor-nav-item.
   * 1) When an anchor-nav-item is clicked, `isActive` is changed to true
   */
  private anchorNavItemClicked(event: MouseEvent) {
    const item = event.target as BlocksAnchorNavItem; /* 1 */
    this.changeActive(item);
  }

  /**
   * Keyboard event listener for Anchor Nav Items
   */
  private handleKeyDown(event: KeyboardEvent) {
    if (event.key === KEYS.ENTER || event.key === KEYS.SPACE) {
      event.preventDefault();
      const item = event.target as BlocksAnchorNavItem;
      this.changeActive(item);

      item.scrollToSection();
    }
  }

  /**
   * Set Active for anchor-nav-item
   * @param catAnchorNavItem - BlocksAnchorNavItem which will be changed to isActive.
   * 1) Store the active anchor-nav-item so that it remains active till it goes out of viewport or some other anchor-nav-item sibling is clicked.
   * 2) Setting aria-active to true for the current active element.
   */
  private changeActive(catAnchorNavItem: BlocksAnchorNavItem) {
    this.catAnchorNavItems?.forEach((item) => {
      if (item === catAnchorNavItem) {
        item.isActive = true;
        this.currentNavItem = item; /* 1 */
        item.setAttribute('aria-current', 'true'); /* 2 */
      } else {
        item.isActive = false;
        item.removeAttribute('aria-current');
      }
    });
  }

  render(): TemplateResult<1> {
    const componentClassNames = this.componentClassNames('cat-c-anchor-nav', {
      'cat-is-mobile': this.isMobile,
      'cat-c-anchor-nav--vertical': this.variant === 'vertical'
    });
    return html`
      <nav part="base" class="${componentClassNames}" aria-label="${this.arialabel}">
        <slot role="list" part="part-default" @click=${this.anchorNavItemClicked} @keydown=${this.handleKeyDown}></slot>
      </nav>
    `;
  }
}

if (customElements.get('cat-anchor-nav') === undefined) {
  customElements.define('cat-anchor-nav', BlocksAnchorNav);
}

declare global {
  interface HTMLElementTagNameMap {
    'cat-anchor-nav': BlocksAnchorNav;
  }
}
